import { captureMetaAttributionFromUrl } from './metaAttribution';

export type DesignMode = 'classic' | 'sketchy';

export type UTMParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  initialDesign?: DesignMode;
  firstSeenAt?: string;
  landingPath?: string;
};

const STORAGE_KEY = 'utm_params';
export const DESIGN_PARAM_KEY = 'design';
export const DESIGN_STORAGE_KEY = 'cassette_design';

export function parseDesignParam(params: URLSearchParams): DesignMode | null {
  const value = params.get(DESIGN_PARAM_KEY);
  if (value === 'classic' || value === 'sketchy') {
    return value;
  }
  return null;
}

export function applyDesignMode(mode: DesignMode): void {
  try {
    window.localStorage.setItem(DESIGN_STORAGE_KEY, mode);
    document.documentElement.classList.toggle('sketchy-redesign', mode === 'sketchy');
  } catch {}
}

export function captureDesignFromUrl(): DesignMode | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const design = parseDesignParam(params);
    if (design) {
      applyDesignMode(design);
    }
    return design;
  } catch {
    return null;
  }
}

function isPaidMetaParams(params: URLSearchParams): boolean {
  return params.get('utm_source') === 'meta' && params.get('utm_medium') === 'paid';
}

export function captureUtmFromLocationToLocalStorage(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    
    // Always capture debug mode if present
    if (params.get('debug_mode') === 'true') {
      localStorage.setItem('ga_debug_mode', 'true');
    }

    const designFromUrl = parseDesignParam(params);
    if (designFromUrl) {
      applyDesignMode(designFromUrl);
    }
    
    // First-touch wins, except paid Meta ad clicks overwrite stale attribution
    const isPaidMetaClick = isPaidMetaParams(params);
    if (localStorage.getItem(STORAGE_KEY) && !isPaidMetaClick) return;
    
    const utm: UTMParams = {};
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
    let found = false;
    for (const key of keys) {
      const value = params.get(key);
      if (value) {
        (utm as any)[key] = value;
        found = true;
      }
    }
    if (!found && !(isPaidMetaClick && designFromUrl)) return;
    if (designFromUrl) {
      utm.initialDesign = designFromUrl;
    }
    utm.firstSeenAt = new Date().toISOString();
    utm.landingPath = window.location.pathname + window.location.search;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
    captureMetaAttributionFromUrl();
  } catch {}
}

export function getStoredUtm(): UTMParams | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as UTMParams;
  } catch {
    return null;
  }
}

export function clearStoredUtm(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

// Check if debug mode should be active
export function isDebugMode(): boolean {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlDebug = urlParams.get('debug_mode') === 'true';
    const storedDebug = localStorage.getItem('ga_debug_mode') === 'true';
    return urlDebug || storedDebug;
  } catch {
    return false;
  }
}

// UTM parameter keys that should never be removed from URL
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

const PRESERVED_ATTRIBUTION_KEYS = [...UTM_KEYS, DESIGN_PARAM_KEY] as const;

/**
 * Extract UTM parameters from current URL
 * Returns a URLSearchParams object with only UTM parameters
 */
export function getUtmParamsFromUrl(): URLSearchParams {
  const currentParams = new URLSearchParams(window.location.search);
  const utmParams = new URLSearchParams();
  
  for (const key of PRESERVED_ATTRIBUTION_KEYS) {
    const value = currentParams.get(key);
    if (value) {
      utmParams.set(key, value);
    }
  }
  
  return utmParams;
}

function getStoredDesignParam(): string | null {
  const utm = getStoredUtm();
  return utm?.initialDesign ?? null;
}

/**
 * Preserve UTM parameters when navigating
 * Use this to build navigation paths that include UTM params
 */
export function preserveUtmParams(path: string): string {
  const utmParams = getUtmParamsFromUrl();
  const storedDesign = getStoredDesignParam();
  if (storedDesign && !utmParams.has(DESIGN_PARAM_KEY)) {
    utmParams.set(DESIGN_PARAM_KEY, storedDesign);
  }

  if (utmParams.toString() === '') {
    return path;
  }

  return mergePathQuery(path, utmParams);
}

/** Merge query params into a path, preserving any existing search and hash. */
export function mergePathQuery(path: string, extra: URLSearchParams): string {
  const hashIndex = path.indexOf('#');
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
  const queryIndex = withoutHash.indexOf('?');
  const pathname = queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const merged = new URLSearchParams(queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : '');
  extra.forEach((value, key) => merged.set(key, value));
  const query = merged.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
}

/**
 * Remove specific params from URL while preserving UTM params
 * Use this instead of manually manipulating URLSearchParams
 */
export function removeParamsPreservingUtm(paramsToRemove: string[]): string {
  const currentParams = new URLSearchParams(window.location.search);
  const utmParams = getUtmParamsFromUrl();
  
  // Remove specified params
  for (const param of paramsToRemove) {
    currentParams.delete(param);
  }
  
  // Ensure UTM params are preserved
  for (const key of PRESERVED_ATTRIBUTION_KEYS) {
    const value = utmParams.get(key);
    if (value) {
      currentParams.set(key, value);
    }
  }
  
  const newSearch = currentParams.toString();
  return newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname;
}

/**
 * Remove UTM params from URL if they're already safely stored in localStorage
 * Use this after Google Drive linking is complete and user is on dashboard
 * Safe to call because:
 * 1. UTM params are already captured to localStorage (first-touch wins)
 * 2. All tracking events use localStorage, not URL params
 * 3. User is past critical onboarding stage
 */
export function cleanUtmFromUrlIfStored(): void {
  try {
    // Only clean if UTM params exist in localStorage
    const storedUtm = getStoredUtm();
    if (!storedUtm || !storedUtm.utm_source) {
      return; // No UTM in storage, keep URL params
    }
    
    // Check if URL has UTM params
    const urlParams = new URLSearchParams(window.location.search);
    let hasUtmInUrl = false;
    for (const key of UTM_KEYS) {
      if (urlParams.has(key)) {
        hasUtmInUrl = true;
        break;
      }
    }
    
    if (!hasUtmInUrl) {
      return; // No UTM in URL, nothing to clean
    }
    
    // Remove UTM params from URL
    for (const key of UTM_KEYS) {
      urlParams.delete(key);
    }
    
    const newSearch = urlParams.toString();
    const newUrl = newSearch 
      ? `${window.location.pathname}?${newSearch}` 
      : window.location.pathname;
    
    window.history.replaceState({}, '', newUrl);
  } catch {
    // Silently fail if anything goes wrong
  }
}


